/**
 * Which account the hub is currently acting as.
 *
 * `team` covers every Group type — pickup, trainer, camp, league, tournament
 * and team. Groups and teams are one concept in this product; "team mode" is
 * simply the name it goes by in the UI. Mirrors mobile's `ActiveMode`.
 *
 * `developer` is the user's developer account: an API organization they own.
 * There is at most one per user, so it carries no id.
 */
export type ActiveAccount =
  | { kind: "individual" }
  | { kind: "team"; teamId: string }
  | { kind: "developer" };

export const INDIVIDUAL_ACCOUNT: ActiveAccount = Object.freeze({
  kind: "individual",
});

export const DEVELOPER_ACCOUNT: ActiveAccount = Object.freeze({
  kind: "developer",
});

const SESSION_PREFIX = "hooper:active-account";
const LAST_PREFIX = "hooper:last-account";

const sessionKey = (userId: string) => `${SESSION_PREFIX}:${userId}`;
const lastKey = (userId: string) => `${LAST_PREFIX}:${userId}`;

const parse = (raw: string | null): ActiveAccount | undefined => {
  if (!raw) return undefined;
  try {
    const value = JSON.parse(raw);
    if (value?.kind === "individual") return INDIVIDUAL_ACCOUNT;
    if (value?.kind === "developer") return DEVELOPER_ACCOUNT;
    if (value?.kind === "team" && typeof value.teamId === "string" && value.teamId) {
      return { kind: "team", teamId: value.teamId };
    }
  } catch {
    // A corrupt value must never block entry — the picker is the fallback.
  }
  return undefined;
};

// Storage throws in some privacy modes; never let that break navigation.
const safeGet = (store: Storage, key: string): string | null => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (store: Storage, key: string, value: string): void => {
  try {
    store.setItem(key, value);
  } catch {
    /* nothing to do — the choice simply does not survive */
  }
};

const safeRemove = (store: Storage, key: string): void => {
  try {
    store.removeItem(key);
  } catch {
    /* nothing to do — storage is unavailable or quota-locked */
  }
};

export const readActiveAccount = (userId: string): ActiveAccount | undefined =>
  parse(safeGet(sessionStorage, sessionKey(userId)));

export const readLastAccount = (userId: string): ActiveAccount | undefined =>
  parse(safeGet(localStorage, lastKey(userId)));

export const writeActiveAccount = (
  userId: string,
  account: ActiveAccount,
): void => {
  const raw = JSON.stringify(account);
  safeSet(sessionStorage, sessionKey(userId), raw);
  safeSet(localStorage, lastKey(userId), raw);
};

/**
 * Forgets only the CURRENT choice (the per-tab one), leaving the remembered
 * "last used" account alone. Sign-out uses this: the point of the separate
 * localStorage key is to pre-highlight the previous choice in the picker, and
 * that convenience should outlive a sign-out — the next sign-in still goes
 * through the picker because the session key is gone.
 */
export const clearActiveAccount = (userId: string): void => {
  safeRemove(sessionStorage, sessionKey(userId));
};

/** Forgets only the remembered "last used" account. */
export const clearLastAccount = (userId: string): void => {
  safeRemove(localStorage, lastKey(userId));
};

